/**
 * Network monitoring utility for debugging SSE connection issues
 */

class NetworkMonitor {
  constructor() {
    this.isOnline = navigator.onLine;
    this.connectionType = this.getConnectionType();
    this.listeners = [];
    this.debugId = `NetworkMonitor-${Date.now()}`;

    // Bind event listeners
    this.handleOnline = this.handleOnline.bind(this);
    this.handleOffline = this.handleOffline.bind(this);
    this.handleConnectionChange = this.handleConnectionChange.bind(this);

    this.init();
  }

  init() {

    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);

    // Listen for connection type changes (if available)
    if (navigator.connection) {
      navigator.connection.addEventListener('change', this.handleConnectionChange);
    }
  }



  getConnectionType() {
    if (!navigator.connection) return 'unknown';

    return {
      effectiveType: navigator.connection.effectiveType,
      type: navigator.connection.type,
      downlink: navigator.connection.downlink,
      rtt: navigator.connection.rtt,
      saveData: navigator.connection.saveData
    };
  }

  handleOnline() {
    this.isOnline = true;
    this.connectionType = this.getConnectionType();
    this.notifyListeners('online');
  }

  handleOffline() {
    this.isOnline = false;
    this.notifyListeners('offline');
  }

  handleConnectionChange() {
    const oldConnectionType = this.connectionType;
    this.connectionType = this.getConnectionType();

    this.notifyListeners('connection-change', {
      old: oldConnectionType,
      new: this.connectionType
    });
  }

  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  notifyListeners(event, data = null) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error(`Error in network listener: ${error.message}`);
      }
    });
  }

  getStatus() {
    return {
      isOnline: this.isOnline,
      connectionType: this.connectionType,
      timestamp: new Date().toISOString()
    };
  }

  // Test network connectivity by making a small request
  async testConnectivity(url = '/favicon.ico') {
    const testStart = performance.now();
    try {
      const response = await fetch(url, {
        method: 'HEAD',
        cache: 'no-cache',
        signal: AbortSignal.timeout(5000) // 5 second timeout
      });

      const testDuration = performance.now() - testStart;
      const isHealthy = response.ok && testDuration < 2000; // Under 2 seconds is healthy

      return {
        success: true,
        responseTime: testDuration,
        status: response.status,
        isHealthy
      };
    } catch (error) {
      const testDuration = performance.now() - testStart;

      return {
        success: false,
        error: error.message,
        responseTime: testDuration,
        isHealthy: false
      };
    }
  }

  destroy() {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);

    if (navigator.connection) {
      navigator.connection.removeEventListener('change', this.handleConnectionChange);
    }

    this.listeners = [];
  }
}

// Create singleton instance
const networkMonitor = new NetworkMonitor();

export default networkMonitor;
